import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import th from "dayjs/locale/th";

import { useAppDispatch, useAppSelector } from "../../state/hook";
import { setApp } from "../../state/libs/appSlice";
import {
  getDistrictService,
  getProvinceService,
} from "../../services/masterData.service";
import { getReportActivitySummaryService } from "../../services/report.service";

import { boxStyle } from "../../constants";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { OverwriteAdapterDayjs } from "../../utils/common.util";
import { StyledTableCell, StyledTableRow } from "../../shared/style";
import { getTask, setTask } from "../../state/libs/taskSlice";

const contentToolbar = {
  toolbar: {
    title: "หน้าหลัก (รายงาน)",
    subtitles: ["รายงานผลออกกำลังกาย"],
  },
};

const ActivitySummary = () => {
  const dispatch = useAppDispatch();
  const taskState = useAppSelector(getTask);

  const [provinces, setProvinces] = useState([]);
  const [provinceId, setProvinceId] = useState<any>("");
  const [districs, setDistrics] = useState([]);
  const [provincesFittered, setProvincesFittered] = useState([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [activities, setActivities] = useState<any[]>([]);
  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);

  const { register, getValues, setValue } = useForm({
    defaultValues: {
      community_code: "",
      type_id: "",
      community_name: "",
      district_id: "",
      province_id: "",
      zone: "",
      from_date: "",
      to_date: "",
    },
  });

  const getProvinces = () => {
    getProvinceService().then((resp) => setProvinces(resp.data));
  };

  const getDistricts = (provinceId: string) => {
    getDistrictService(provinceId).then((resp) => setDistrics(resp.data));
  };

  const handleOnSearch = () => {
    getReportActivity();
  };

  const handleZoneChange = (event: SelectChangeEvent) => {
    setProvincesFittered(
      !event.target.value
        ? provinces
        : provinces.filter((t: any) => t.zone === parseInt(event.target.value))
    );
    setProvinceId("");
    setValue("province_id", "");
    setValue("district_id", "");

    setDistrics([]);
  };

  const handleProvinceChange = (event: SelectChangeEvent) => {
    setProvinceId(event.target.value);
    getDistricts(event.target.value);
    setValue("district_id", "");
  };

  const handleFromDateChange = (value: any) => {
    const date = dayjs(value).format("YYYY-MM-DD");
    setFromDate(date);
    setValue("from_date", date);
  };

  const handleToDateChange = (value: any) => {
    const date = dayjs(value).format("YYYY-MM-DD");
    setToDate(date);
    setValue("to_date", date);
  };

  const handleExportReport = () => {
    dispatch(
      setTask({
        id: "activitySummaryReport",
        reportName: "activitySummaryReport",
        process: true,
        processing: false,
        filters: getValues(),
      })
    );
  };

  const getReportActivity = () => {
    setLoading(true);
    getReportActivitySummaryService(getValues()).then((resp) => {
      setLoading(false);
      setActivities(resp.data);
    });
  };

  const setContentToolbar = () => {
    dispatch(setApp(contentToolbar));
  };

  useEffect(setContentToolbar, [dispatch]);

  useEffect(getProvinces, []);

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Box sx={boxStyle}>
        <Box
          display="flex"
          justifyContent="space-between"
          gap="16px"
          flexDirection={{ xs: "column", md: "row" }}
        >
          <Box display="flex" flexDirection="column" gap={4} width={1}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="zone-label">เขตสุขภาพ</InputLabel>
                  <Select
                    labelId="zone-label"
                    id="province"
                    label="เขตสุขภาพ"
                    {...register("zone")}
                    onChange={handleZoneChange}
                  >
                    <MenuItem key={0} value="">
                      ทั้งหมด
                    </MenuItem>
                    {Array.from(Array(13).keys()).map((index: any) => (
                      <MenuItem key={index} value={(index + 1).toString()}>
                        {"เขตสุขภาพที่ " + (index + 1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="province-label">จังหวัด</InputLabel>
                  <Select
                    labelId="province-label"
                    id="province"
                    label="จังหวัด"
                    value={provinceId}
                    {...register("province_id")}
                    onChange={handleProvinceChange}
                  >
                    <MenuItem key={0} value="">
                      ทั้งหมด
                    </MenuItem>
                    {provincesFittered.map(
                      (p: { id: number; name: string }) => (
                        <MenuItem key={p.id} value={p.id.toString()}>
                          {p.name}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="district-label">อำเภอ</InputLabel>
                  <Select
                    labelId="district-label"
                    id="district"
                    label="อำเภอ"
                    {...register("district_id", { required: true })}
                  >
                    <MenuItem key={0} value="">
                      ทั้งหมด
                    </MenuItem>
                    {districs.map((d: { id: number; name: string }) => (
                      <MenuItem key={d.id} value={d.id.toString()}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <LocalizationProvider
                    dateAdapter={OverwriteAdapterDayjs}
                    adapterLocale={th}
                  >
                    <DatePicker
                      label="วันเริ่ม"
                      views={["year", "month", "day"]}
                      inputFormat="DD/MMM/BBBB"
                      value={fromDate}
                      onChange={handleFromDateChange}
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: "วว/ดด/ปปปป",
                            readOnly: true,
                          }}
                          {...register("from_date", { required: true })}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <LocalizationProvider
                    dateAdapter={OverwriteAdapterDayjs}
                    adapterLocale={th}
                  >
                    <DatePicker
                      label="วันสิ้นสุด"
                      views={["year", "month", "day"]}
                      inputFormat="DD/MMM/BBBB"
                      value={toDate}
                      onChange={handleToDateChange}
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: "วว/ดด/ปปปป",
                            readOnly: true,
                          }}
                          {...register("to_date", { required: true })}
                        />
                      )}
                    ></DatePicker>
                  </LocalizationProvider>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Box display="flex" gap="8px" height="fit-content">
            <Box sx={{ position: "relative" }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon sx={{ color: "white" }} />}
                color="green"
                disabled={loading}
                onClick={handleOnSearch}
              >
                <Typography color="white">ค้นหา</Typography>
              </Button>
              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>

            <Box sx={{ position: "relative" }}>
              <Button
                variant="contained"
                color="blue"
                disabled={taskState.tasks.find(
                  (task: any) => task.id === "activitySummaryReport"
                )}
                onClick={handleExportReport}
              >
                <Typography color="white">EXPORT</Typography>
              </Button>
              {taskState.tasks.find(
                (task: any) => task.id === "activitySummaryReport"
              ) && (
                <CircularProgress
                  color="blue"
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={boxStyle}>
        <TableContainer
          component={Paper}
          sx={{ boxShadow: "none", borderRadius: 0 }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>ลำดับ</StyledTableCell>
                <StyledTableCell>ที่อยู่</StyledTableCell>
                <StyledTableCell align="right">จำนวนคนส่ง</StyledTableCell>
                <StyledTableCell align="right">
                  ใช้พลังงาน(kcal)
                </StyledTableCell>
                <StyledTableCell align="right">ระยะเวลา(นาที)</StyledTableCell>
                <StyledTableCell align="right">
                  ระยะทาง(กิโลเมตร)
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities.map((row, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell component="th" scope="row">
                    {index + 1}
                  </StyledTableCell>
                  <StyledTableCell>
                    {typeof row.location === "number"
                      ? `เขตสุขภาพที่ ${row.location}`
                      : row.location}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {row.member_count.toLocaleString()}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {row.total_calories.toLocaleString()}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {row.total_minutes.toLocaleString()}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {row.total_distances.toLocaleString()}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
              {!activities.length && (
                <StyledTableRow>
                  <StyledTableCell
                    component="th"
                    scope="row"
                    colSpan={6}
                    align="center"
                  >
                    ไม่พบข้อมูล
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default ActivitySummary;
